import { useState, useEffect, FC, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import usePath from 'helpers/hooks/usePath';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { toUIColor } from 'helpers/mappers/toUIColor';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUISize } from 'helpers/mappers/toUIsize';
import { ImageProps } from 'frontastic/lib/image';
import ProductDetails, { ProductDetailsProps } from '..';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import { PersonalisationBalls } from 'types/datasource';

type ProductDetailsAdapterProps = {
  product: Product;
  productLogo?: string;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  setHoveredImage?: undefined;
  setVariantForSeo?: (variant: Variant) => void;
  trustBoxCss?: TrustBoxCss;
  personalisationBalls?: PersonalisationBalls;
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  productLogo,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  setVariantForSeo,
  trustBoxCss,
  personalisationBalls,
}) => {
  const { path } = usePath();
  const searchParams = useSearchParams();
  const variantSku = searchParams.get('sku') ?? '';
  const variantCode = searchParams.get('variantCode') ?? '';
  const [variant, setVariant] = useState<Variant>();
  const [mappedProduct, setMappedProduct] = useState<UIProduct>();
  const [customFitPrice, setCustomFitPrice] = useState<number | null>(null);
  const [colorVariant, setColorVariant] = useState<Variant | undefined>();

  usePreloadImages(product?.variants?.map((variant) => variant?.images ?? [])?.flat());
  const availableVariant = variant || product?.variants?.[0];
  useEffect(() => {
    if (product && availableVariant) {
      const colors = toUIColor(product);
      const sizes = toUISize(product);
      const productToUse = toUIProduct(product, availableVariant, colors, sizes);
      setMappedProduct({ ...productToUse, images: [productToUse.images[0]] });
    }
  }, [product, variant]);

  useEffect(() => {
    if (!product) return;
    if (inModalVersion) {
      // setVariant(product?.variants[0]);
      if (variantCode) {
        const targetVariant = product?.variants?.find(({ attributes }) => attributes?.variantCode == variantCode);
        setColorVariant(targetVariant);
      }
    } else {
      // const targetVariant = product?.variants?.find(({ sku }) => sku == variantSku);
      // setVariant(targetVariant ?? product?.variants?.[0]);
      if (variantSku) {
        const targetVariant = product?.variants?.find(({ sku }) => sku == variantSku);
        setVariant(targetVariant);
      } else if (variantCode) {
        const targetVariant = product?.variants?.find(({ attributes }) => attributes?.variantCode == variantCode);
        setColorVariant(targetVariant);
      }
    }
  }, [inModalVersion, product, path, variantSku, variantCode]);

  const handleChangeVariant = useCallback(
    (sku: string) => {
      const variantsToUse = product?.variants?.find((variant) => variant?.sku === sku);
      setVariant(variantsToUse);
      if (setVariantForSeo) {
        setVariantForSeo(variantsToUse as Variant);
      }
    },
    [product?.variants, setVariantForSeo],
  );

  if (!product || !availableVariant) return <></>;

  return (
    <ProductDetails
      pdpProduct={mappedProduct as UIProduct}
      product={mappedProduct as UIProduct}
      productLogo={productLogo}
      variant={variant}
      url={product._url}
      inModalVersion={inModalVersion}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
      setHoveredImage={undefined}
      setCustomFitPrice={setCustomFitPrice}
      customFitPrice={customFitPrice}
      setColorVariant={setColorVariant}
      colorVariant={colorVariant}
      trustBoxCss={trustBoxCss}
      personalisationBalls={personalisationBalls}
    />
  );
};

export default ProductDetailsAdapter;
